
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import type { PackageData } from '@/pages/CreatePackage';
import { Edit, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAirlines } from '@/hooks/useTasks';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';

type Flight = PackageData['flights'][0];

interface EditFlightDialogProps {
  flight: Flight;
  index: number;
  onUpdateFlight: (index: number, flight: Flight) => void;
}

export const EditFlightDialog = ({ flight, index, onUpdateFlight }: EditFlightDialogProps) => {
  const [open, setOpen] = useState(false);
  const [editedFlight, setEditedFlight] = useState<Flight & { 
    costPriceStr: string; 
    sellingPriceStr: string; 
  }>({
    ...flight,
    costPriceStr: flight.costPrice?.toString() || '',
    sellingPriceStr: flight.sellingPrice?.toString() || ''
  });
  
  const { airlines, isLoading: isLoadingAirlines } = useAirlines();
  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedFlight(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: keyof Flight) => (value: string) => {
    setEditedFlight(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name: 'adults' | 'children' | 'infants') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setEditedFlight(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const updatedFlight: Flight = {
      ...editedFlight,
      costPrice: parseFloat(editedFlight.costPriceStr) || 0,
      sellingPrice: parseFloat(editedFlight.sellingPriceStr) || 0,
      price: parseFloat(editedFlight.sellingPriceStr) || 0
    };
    
    onUpdateFlight(index, updatedFlight);
    setOpen(false);
    toast.success("تم تحديث رحلة الطيران بنجاح.");
  };

  const filteredFromCities = cities.filter(city => city.country_id === editedFlight.fromCountry);
  const filteredToCities = cities.filter(city => city.country_id === editedFlight.toCountry);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل رحلة الطيران</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Flight Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="type">نوع الرحلة</Label>
              <Select name="type" value={editedFlight.type} onValueChange={handleSelectChange('type')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outbound">ذهاب</SelectItem>
                  <SelectItem value="return">عودة</SelectItem>
                  <SelectItem value="internal">داخلية</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="airline">شركة الطيران</Label>
              <Select name="airline" value={editedFlight.airline} onValueChange={handleSelectChange('airline')}>
                <SelectTrigger disabled={isLoadingAirlines}>
                  <SelectValue placeholder="اختر شركة طيران" />
                </SelectTrigger>
                <SelectContent>
                  {airlines?.map(a => (
                    <SelectItem key={a.id} value={a.name_ar}>
                      {a.name_ar} ({a.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="class">درجة السفر</Label>
              <Select name="class" value={editedFlight.class} onValueChange={handleSelectChange('class')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Economy">سياحية</SelectItem>
                  <SelectItem value="Business">رجال أعمال</SelectItem>
                  <SelectItem value="First">أولى</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Origin Details */}
          <div className="space-y-2">
            <Label className="text-base font-medium">تفاصيل المغادرة</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="fromCountry">دولة المغادرة</Label>
                <Select value={editedFlight.fromCountry} onValueChange={handleSelectChange('fromCountry')}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الدولة" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="fromCity">مدينة المغادرة</Label>
                <Select value={editedFlight.fromCity} onValueChange={handleSelectChange('fromCity')}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر المدينة" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredFromCities.map(city => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="from">مطار المغادرة</Label>
                <Input id="from" name="from" value={editedFlight.from} onChange={handleChange} placeholder="مثال: RUH" required />
              </div>
            </div>
          </div>

          {/* Destination Details */}
          <div className="space-y-2">
            <Label className="text-base font-medium">تفاصيل الوصول</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="toCountry">دولة الوصول</Label>
                <Select value={editedFlight.toCountry} onValueChange={handleSelectChange('toCountry')}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الدولة" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="toCity">مدينة الوصول</Label>
                <Select value={editedFlight.toCity} onValueChange={handleSelectChange('toCity')}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر المدينة" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredToCities.map(city => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="to">مطار الوصول</Label>
                <Input id="to" name="to" value={editedFlight.to} onChange={handleChange} placeholder="مثال: DXB" required />
              </div>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">التاريخ</Label>
              <Input id="date" name="date" type="date" value={editedFlight.date} onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="time">الوقت</Label>
              <Input id="time" name="time" type="time" value={editedFlight.time} onChange={handleChange} required />
            </div>
          </div>

          {/* Passengers */}
          <div className="space-y-2">
            <Label className="text-base font-medium">عدد المسافرين</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="adults">عدد البالغين</Label>
                <Input id="adults" name="adults" type="number" min="1" value={editedFlight.adults} onChange={handleNumberChange('adults')} required />
              </div>
              <div>
                <Label htmlFor="children">عدد الأطفال</Label>
                <Input id="children" name="children" type="number" min="0" value={editedFlight.children} onChange={handleNumberChange('children')} />
              </div>
              <div>
                <Label htmlFor="infants">عدد الرضع</Label>
                <Input id="infants" name="infants" type="number" min="0" value={editedFlight.infants} onChange={handleNumberChange('infants')} />
              </div>
            </div>
          </div>

          {/* Transit Flight */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 space-x-reverse">
              <Checkbox
                id="transitFlight"
                checked={editedFlight.transitFlight}
                onCheckedChange={(checked) => setEditedFlight(prev => ({ ...prev, transitFlight: checked as boolean }))}
              />
              <Label htmlFor="transitFlight" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                رحلة ترانزيت (انتظار)
              </Label>
            </div>

            {editedFlight.transitFlight && (
              <div>
                <Label htmlFor="transitDuration">مدة الانتظار (ساعات)</Label>
                <Input 
                  id="transitDuration" 
                  name="transitDuration" 
                  type="number" 
                  min="0" 
                  step="0.5" 
                  value={editedFlight.transitDuration} 
                  onChange={(e) => setEditedFlight(prev => ({ ...prev, transitDuration: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}
          </div>

          {/* Supplier */}
          <div>
            <Label htmlFor="supplier">المورد</Label>
            <Input id="supplier" name="supplier" value={editedFlight.supplier} onChange={handleChange} placeholder="اسم المورد" />
          </div>

          {/* Pricing */}
          <div className="space-y-2">
            <Label className="text-base font-medium">التسعير</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="costPriceStr">سعر التكلفة (SAR)</Label>
                <Input id="costPriceStr" name="costPriceStr" type="number" min="0" step="0.01" value={editedFlight.costPriceStr} onChange={handleChange} placeholder="0.00" />
              </div>
              <div>
                <Label htmlFor="sellingPriceStr">سعر البيع (SAR)</Label>
                <Input id="sellingPriceStr" name="sellingPriceStr" type="number" min="0" step="0.01" value={editedFlight.sellingPriceStr} onChange={handleChange} placeholder="0.00" required />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">ملاحظات الموظف</Label>
            <Textarea 
              id="notes" 
              name="notes" 
              value={editedFlight.notes} 
              onChange={handleChange} 
              placeholder="أدخل أي ملاحظات إضافية..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              إلغاء
            </Button>
            <Button type="submit">
              حفظ التعديلات
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
